import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import swal from 'sweetalert'
import {
	appAcceptsansthaMembership,
	appDeletesansthaMembership,
	appGetAllSanstha,
	appGetSansthaById,
	appRejctedsansthaMembership,
} from '../../store/sanstha'
import Paginate from '../../components/pagination/paginate'
import Select from 'react-select'
import { appAllGetHubs } from '../../store/hubs'
import { appPendingMembership } from '../../store/pendingmembers'
import PostPreview from '../../components/PostPreview/PostPreview'
import { toCamelCase } from '../../utils/Function'

function TableFilter() {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const params = useParams()
	const [showUserModal, setShowUserModal] = useState(false)
	const [showAcceptModal, setShowAcceptModal] = useState(false)
	const [acceptDetails, setAcceptDetails] = useState(null)
	const [acceptForm, setAcceptForm] = useState({
		membershipStartDate: '',
		membershipEndDate: '',
		membershipType: '',
	})
	const [acceptError, setAcceptError] = useState('')
	const [membershipOptions, setMembershipOptions] = useState([])
	const [rejectionReason, setRejectionReason] = useState('')
	const [rejectionError, setRejectionError] = useState('')
	const [rejectedId, setRejectedId] = useState(null)
	const [page, setPage] = useState(params.page || 1)
	const [activeFilters, setActiveFilters] = useState({})
	const [selectedFilters, setSelectedFilters] = useState({
		sanstha: null,
		hub: null,
		startDate: null,
		endDate: null,
	})
	const [error, setError] = useState('')
	const hub = useSelector(state => state?.hubReducer.hub)
	const sanstha = useSelector(state => state?.sansthaReducer.sanstha)
	const paginate = useSelector(state => state.PendingMembershipReducer.paginate)
	const pendingMembership = useSelector(state => state.PendingMembershipReducer.pendingMembership)
	useEffect(() => {
		dispatch(appGetAllSanstha({ page: 1, limit: 1000, loading: false }))
		dispatch(appAllGetHubs({ page: 1, limit: 1000, loading: false }))
	}, [page])
	useEffect(() => {
		const searchParams = {
			...activeFilters,
			page,
			limit: 10,
		}
		dispatch(appPendingMembership(searchParams))
		navigate(`/requsteduser/${page}`)
	}, [page, activeFilters])

	const handleSearch = useCallback(() => {
		if (selectedFilters.startDate && !selectedFilters.endDate) {
			setError('Please select an end date.')
			return
		}

		setError('')
		const filters = {}
		if (selectedFilters.hub) {
			filters.hub = selectedFilters.hub.value
		}
		if (selectedFilters.sanstha) {
			filters.sanstha = selectedFilters.sanstha.value
		}
		if (selectedFilters.startDate) {
			filters.startDate = selectedFilters.startDate
		}
		if (selectedFilters.endDate) {
			filters.endDate = selectedFilters.endDate
		}
		setActiveFilters(filters)
		setPage(1)
	}, [selectedFilters])

	const handleDeletesansthaMembership = membershipId => {
		swal({
			title: 'Are you sure?',
			text: 'You want to delete this Sanstha Membership?',
			icon: 'warning',
			buttons: true,
			dangerMode: true,
		}).then(willDelete => {
			if (willDelete) {
				dispatch(
					appDeletesansthaMembership({
						id: membershipId,
					}),
				).then(() => {
					dispatch(appPendingMembership({ page: 1, limit: 10 }))
				})
			}
		})
	}

	const handleRejectedsansthaMembership = id => {
		setRejectedId(id)
		setShowUserModal(true)
	}

	const formatDateForInput = value => {
		if (!value) return ''
		const date = new Date(value)
		if (Number.isNaN(date.getTime())) return ''
		const year = date.getFullYear()
		const month = String(date.getMonth() + 1).padStart(2, '0')
		const day = String(date.getDate()).padStart(2, '0')
		return `${year}-${month}-${day}`
	}

	const formatDisplayDate = value => {
		if (!value) return '-'

		if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
			const [year, month, day] = value.split('T')[0].split('-')
			return `${day}-${month}-${year}`
		}

		const date = new Date(value)
		if (Number.isNaN(date.getTime())) return value

		return date.toLocaleDateString('en-GB')
	}

	const getSubscriptionDates = (type, startValue) => {
		const startDate = startValue ? new Date(startValue) : new Date()
		const endDate = new Date(startDate)

		switch (type) {
			case 'Monthly':
				endDate.setMonth(endDate.getMonth() + 1)
				break
			case 'Yearly':
				endDate.setFullYear(endDate.getFullYear() + 1)
				break
			case 'Lifetime':
			default:
				endDate.setFullYear(9999, 11, 31)
				break
		}

		return {
			membershipStartDate: formatDateForInput(startDate),
			membershipEndDate: formatDateForInput(endDate),
			membershipType: type,
		}
	}

	const handleMembershipStartDateChange = value => {
		setAcceptForm(prev => {
			if (!prev.membershipType) {
				return {
					...prev,
					membershipStartDate: value,
					membershipEndDate:
						prev.membershipEndDate && prev.membershipEndDate < value ? '' : prev.membershipEndDate,
				}
			}

			return getSubscriptionDates(prev.membershipType, value)
		})
	}

	const handleMembershipTypeChange = type => {
		setAcceptForm(prev => {
			if (!type) {
				return {
					...prev,
					membershipType: '',
				}
			}

			return getSubscriptionDates(type, prev.membershipStartDate)
		})
	}

	const handleAcceptedsansthaMembership = async item => {
		setAcceptDetails(item)
		setAcceptForm({
			membershipStartDate: formatDateForInput(item?.membershipStartDate),
			membershipEndDate: formatDateForInput(item?.membershipEndDate),
			membershipType: item?.membershipType || '',
		})
		setAcceptError('')
		setMembershipOptions([])
		setShowAcceptModal(true)

		const sansthaId = item?.sansthaId?._id
		if (!sansthaId) return

		try {
			const response = await dispatch(appGetSansthaById(sansthaId)).unwrap()
			const memberships = response?.data?.membership || []

			console.log('Fetched memberships:', memberships)
			setMembershipOptions(Array.isArray(memberships) ? memberships : [])
		} catch (error) {
			setAcceptError('Unable to load membership options. Please try again.')
		}
	}

	const handleCloseAcceptModal = () => {
		setShowAcceptModal(false)
		setAcceptDetails(null)
		setAcceptForm({
			membershipStartDate: '',
			membershipEndDate: '',
			membershipType: '',
		})
		setAcceptError('')
		setMembershipOptions([])
	}

	const handleSubmitAccept = () => {
		if (
			!acceptForm.membershipStartDate ||
			!acceptForm.membershipEndDate ||
			!acceptForm.membershipType
		) {
			setAcceptError('Start date, end date and membership type are required.')
			return
		}

		if (acceptForm.membershipEndDate < acceptForm.membershipStartDate) {
			setAcceptError('Membership end date must be after start date.')
			return
		}

		setAcceptError('')
		dispatch(
			appAcceptsansthaMembership({
				membershipId: acceptDetails?._id,
				membershipStartDate: acceptForm.membershipStartDate,
				membershipEndDate: acceptForm.membershipEndDate,
				membershipType: acceptForm.membershipType,
				amount: selectedMembershipOption?.membershipfee || acceptDetails?.amount,
			}),
		).then(() => {
			dispatch(appPendingMembership({ page: 1, limit: 10 }))
			handleCloseAcceptModal()
		})
	}

	const hnadleSubmitRected = () => {
		if (!rejectionReason.trim()) {
			setRejectionError('Reason is required.')
			return
		}
		setRejectionError('')
		dispatch(
			appRejctedsansthaMembership({
				membershipId: rejectedId,
				rejectionreason: rejectionReason,
			}),
		).then(() => {
			dispatch(appPendingMembership({ page: 1, limit: 10 }))
		})
		setShowUserModal(false)
		setRejectionReason('')
		setRejectedId(null)
	}

	const handleCloseModal = () => {
		setShowUserModal(false)
		setRejectionReason('')
		setRejectionError('')
		setRejectedId(null)
	}

	const selectedMembershipOption = membershipOptions.find(
		membership => membership?.membershipType === acceptForm.membershipType,
	)

	const isUpgradeMembership = item =>
		item?.upgradeMembership === true || item?.upgradeMembership === 'true'

	const renderMembershipType = item => {
		const appliedMembership = item?.membershipType || '-'
		const renderDateRange = (startDate, endDate) => (
			<div
				style={{
					marginTop: '6px',
					color: '#6f7f8f',
					fontSize: '11px',
					fontWeight: '600',
					lineHeight: '1.5',
				}}
			>
				<div>Start: {formatDisplayDate(startDate)}</div>
				<div>End: {formatDisplayDate(endDate)}</div>
			</div>
		)

		if (!isUpgradeMembership(item)) {
			return (
				<div
					style={{
						width: '300px',
						border: '1px solid #d8e2ee',
						borderRadius: '6px',
						backgroundColor: '#f8fbff',
						padding: '14px 12px',
					}}
				>
					<div
						style={{
							color: '#687786',
							fontSize: '11px',
							fontWeight: '700',
							marginBottom: '8px',
						}}
					>
						First Time Membership
					</div>
					<div
						style={{
							color: '#1f2d3d',
							fontSize: '13px',
							fontWeight: '700',
						}}
					>
						{appliedMembership}
					</div>
					{renderDateRange(item?.membershipStartDate, item?.membershipEndDate)}
				</div>
			)
		}

		const existingMembership = item?.previousMembershipType || '-'

		return (
			<div
				style={{
					display: 'grid',
					gridTemplateColumns: '1fr 1fr',
					width: '300px',
					border: '1px solid #d8e2ee',
					borderRadius: '6px',
					overflow: 'hidden',
					backgroundColor: '#f8fbff',
				}}
			>
				<div>
					<div
						style={{
							minHeight: '48px',
							padding: '9px 12px',
							backgroundColor: '#eef3f9',
							color: '#44546a',
							fontSize: '11px',
							fontWeight: '700',
							textTransform: 'uppercase',
							borderRight: '1px solid #d8e2ee',
							borderBottom: '1px solid #d8e2ee',
						}}
					>
						Existing Membership
					</div>
					<div
						style={{
							minHeight: '42px',
							padding: '12px',
							backgroundColor: '#fff',
							color: '#52657a',
							fontSize: '13px',
							fontWeight: '700',
							borderRight: '1px solid #d8e2ee',
						}}
					>
						{existingMembership}
						{renderDateRange(
							item?.previousMembershipStartDate,
							item?.previousMembershipEndDate,
						)}
					</div>
				</div>
				<div>
					<div
						style={{
							minHeight: '48px',
							padding: '9px 12px',
							backgroundColor: '#eef3f9',
							color: '#44546a',
							fontSize: '11px',
							fontWeight: '700',
							textTransform: 'uppercase',
							borderBottom: '1px solid #d8e2ee',
						}}
					>
						Applied Membership
					</div>
					<div
						style={{
							minHeight: '42px',
							padding: '12px',
							backgroundColor: '#edfff5',
							color: '#176c38',
							fontSize: '13px',
							fontWeight: '700',
						}}
					>
						{appliedMembership}
						{renderDateRange(item?.membershipStartDate, item?.membershipEndDate)}
					</div>
				</div>
			</div>
		)
	}

	return (
		<>
			<div className="card dataTables_wrapper">
				<div className="card-body">
					<div className="row">
						<div className="col-md-2">
							<div className="form-group">
								<label>Community</label>
								<Select
									options={
										Array.isArray(hub.data)
											? hub.data.map(hub => ({
													value: hub._id,
													label: toCamelCase(hub?.name),
											  }))
											: []
									}
									onChange={option => {
										setSelectedFilters(prev => ({
											...prev,
											hub: option,
										}))
									}}
									value={selectedFilters.hub}
									placeholder="Select Community.."
									getOptionLabel={option => option.label}
									getOptionValue={option => option.value}
								/>
							</div>
						</div>

						<div className="col-md-2">
							<div className="form-group">
								<label>Sanstha</label>
								<Select
									options={
										Array.isArray(sanstha)
											? sanstha.map(sanstha => ({
													value: sanstha._id,
													label: toCamelCase(sanstha?.name),
											  }))
											: []
									}
									onChange={option => {
										setSelectedFilters(prev => ({
											...prev,
											sanstha: option,
										}))
									}}
									value={selectedFilters.sanstha}
									placeholder="Select Sanstha.."
									getOptionLabel={option => option?.name || option?.label}
									getOptionValue={option => option?._id || option?.value}
								/>
							</div>
						</div>

						<div className="col-md-2">
							<div className="form-group">
								<label>Start Date</label>
								<input
									type="date"
									className="form-control"
									value={selectedFilters.startDate || ''}
									max={new Date().toISOString().split('T')[0]}
									onChange={e => {
										const value = e.target.value
										setSelectedFilters(prev => ({
											...prev,
											startDate: value,
											endDate: prev.endDate && prev.endDate < value ? '' : prev.endDate,
										}))
									}}
								/>
							</div>
						</div>

						<div className="col-md-2">
							<div className="form-group">
								<label>End Date</label>
								<input
									type="date"
									className="form-control"
									min={selectedFilters.startDate || ''}
									max={new Date().toISOString().split('T')[0]}
									value={selectedFilters.endDate || ''}
									onChange={e => {
										const value = e.target.value
										setSelectedFilters(prev => ({
											...prev,
											endDate: value,
										}))
										if (value) {
											setError('')
										}
									}}
								/>
								{error && <span className="text-danger">{error}</span>}
							</div>
						</div>

						<div className="d-flex" style={{ alignItems: 'center', marginTop: '12px' }}>
							<button
								className="btn btn-primary mr-2"
								onClick={handleSearch}
								style={{ marginRight: '8px' }}
							>
								<em className="fas fa-search"></em> Search
							</button>
							<button
								className="btn btn-secondary"
								onClick={() => {
									setSelectedFilters({
										sanstha: null,
										hub: null,
										startDate: null,
										endDate: null,
									})
									setActiveFilters({})
									setPage(1)
								}}
							>
								<em className="fas fa-redo"></em> Reset
							</button>
						</div>
					</div>
					<div className="table-responsive">
						<table className="table table-bordered table-hover">
							<thead>
								<tr>
									<th>Community</th>
									<th>Sanstha</th>
									<th>User</th>
									<th data-column-id="sender">Mobile number </th>
									<th>Membership Type</th>
									<th>Honorary Answer</th>
									<th>Amount</th>
									<th data-column-id="received" data-order="desc">
										Documents
									</th>

									<th>Existing Member</th>
									<th data-column-id="received" data-order="desc">
										Status
									</th>

									<th>payment Reference No</th>
									<th>payment Date</th>
									<th data-column-id="commands" data-formatter="commands" data-sortable="false">
										Action
										<div></div>
									</th>
								</tr>
							</thead>
							{pendingMembership.data?.map(item => (
								<tbody key={item?._id}>
									<tr>
										<td>
											<PostPreview value={item?.hub?.image} />

											{item?.hub?.name}
										</td>
										<td>
											{' '}
											<PostPreview value={item?.sansthaId?.image} />
											{item?.sansthaId?.name}
										</td>
										<td>
											<PostPreview value={item?.userId?.profilePic || '/img/user.jpg'} />
											{item?.userId?.firstName} {item?.userId?.lastName}
										</td>
										<td> {item?.userId?.mobile}</td>
										<td>{renderMembershipType(item)}</td>
										<td>{item?.honoraryAnswer}</td>
										<td>
											{' '}
											₹ <span style={{ fontWeight: 'bold' }}>{item?.amount}</span>
											<br />
											{item?.paymentMode && <small>Mode: {item?.paymentMode}</small>}
										</td>
										<td>
											<PostPreview value={item?.paymentScreenshot} />
										</td>

										<td>
											{(item?.existingMember === true || item?.existingMember === 'true') && (
												<span className="badge badge-primary">Yes</span>
											)}
											{(item?.existingMember === false || item?.existingMember === 'false') && (
												<span className="badge badge-primary">No</span>
											)}
										</td>
										<td>
											{item?.status === 'Pending' && (
												<span className="badge badge-primary">Pending</span>
											)}
											{item?.status === 'Accepted' && (
												<span className="badge badge-success">Accepted</span>
											)}
											{item?.status === 'Rejected' && (
												<span className="badge badge-danger">Rejected</span>
											)}
										</td>

										<td>{item?.paymentRef}</td>
										<td>
											{item.paymentDate}
											{/* {item.paymentDate &&
                        new Date(item.paymentDate).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })} */}
										</td>
										<td>
											{item.status === 'Pending' ? (
												<div style={{ display: 'flex', gap: '5px' }}>
													<button
														type="button"
														className="btn btn-sm btn-green command-delete"
														data-row-id="10253"
														onClick={() => handleAcceptedsansthaMembership(item)}
													>
														<i className="fa fa-check" aria-hidden="true">
															{' '}
															Update & Accept
														</i>
													</button>
													<button
														type="button"
														className="btn btn-sm btn-danger command-delete"
														data-row-id="10253"
														onClick={() => handleRejectedsansthaMembership(item?._id)}
													>
														<i className="fa fa-ban" aria-hidden="true">
															{' '}
															Reject
														</i>
													</button>
												</div>
											) : (
												<button
													type="button"
													className="btn btn-sm btn-danger command-delete"
													data-row-id="10253"
													onClick={() => handleDeletesansthaMembership(item?._id)}
												>
													<em className="fa fa-trash fa-fw"></em>
												</button>
											)}
										</td>
									</tr>
								</tbody>
							))}
						</table>
					</div>
					{paginate && <Paginate paginate={paginate} page={page} setPage={setPage} />}
				</div>
			</div>
			{showAcceptModal && (
				<div
					className="modal fade show"
					style={{
						display: 'block',
						backgroundColor: 'rgba(0,0,0,0.5)',
						top: '50px',
					}}
				>
					<div className="modal-dialog">
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title">Update & Accept Membership</h5>
								<button type="button" className="close" onClick={handleCloseAcceptModal}>
									<span>&times;</span>
								</button>
							</div>
							<div className="modal-body">
								<div
									className="d-flex align-items-center mb-3 pb-3"
									style={{ borderBottom: '1px solid #e5e7eb' }}
								>
									<PostPreview value={acceptDetails?.userId?.profilePic || '/img/user.jpg'} />
									<div className="ml-2">
										<div style={{ fontWeight: '600' }}>
											{acceptDetails?.userId?.firstName} {acceptDetails?.userId?.lastName}
										</div>
										{acceptDetails?.userId?.mobile && (
											<small className="text-muted">{acceptDetails.userId.mobile}</small>
										)}
									</div>
								</div>
								<div className="form-group">
									<label>Membership Start Date</label>
									<input
										type="date"
										className="form-control"
										value={acceptForm.membershipStartDate}
										onChange={e => handleMembershipStartDateChange(e.target.value)}
									/>
								</div>

								<div className="form-group">
									<label>Membership End Date</label>
									<input
										type="date"
										className="form-control"
										min={acceptForm.membershipStartDate || ''}
										value={acceptForm.membershipEndDate}
										onChange={e =>
											setAcceptForm(prev => ({
												...prev,
												membershipEndDate: e.target.value,
											}))
										}
									/>
								</div>

								<div className="form-group">
									<label>Membership</label>
									<select
										className="form-control"
										value={acceptForm.membershipType}
										onChange={e => handleMembershipTypeChange(e.target.value)}
									>
										<option value="">Select Membership</option>
										{membershipOptions.map(membership => (
											<option
												key={membership?._id || membership?.membershipType}
												value={membership?.membershipType}
											>
												{membership?.membershipType}
												{membership?.membershipfee ? ` - Rs. ${membership.membershipfee}` : ''}
											</option>
										))}
										{acceptForm.membershipType && !selectedMembershipOption && (
											<option value={acceptForm.membershipType}>{acceptForm.membershipType}</option>
										)}
									</select>
									{selectedMembershipOption?.membershipfee && (
										<small className="text-muted">
											Fee: Rs. {selectedMembershipOption.membershipfee}
										</small>
									)}
								</div>

								{acceptError && <div className="text-danger">{acceptError}</div>}
							</div>
							<div className="modal-footer">
								<button
									type="button"
									className="btn btn-secondary"
									onClick={handleCloseAcceptModal}
								>
									Cancel
								</button>
								<button type="button" className="btn btn-primary" onClick={handleSubmitAccept}>
									Update & Accept
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
			{showUserModal && (
				<div
					className="modal fade show"
					style={{
						display: 'block',
						backgroundColor: 'rgba(0,0,0,0.5)',
						top: '50px',
					}}
				>
					<div className="modal-dialog">
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title"> Rejection </h5>
								<button type="button" className="close" onClick={handleCloseModal}>
									<span>&times;</span>
								</button>
							</div>
							<div className="modal-body">
								<div className="form-group">
									<label>Reason of rejection</label>
									<textarea
										className="form-control"
										name="description"
										value={rejectionReason}
										onChange={e => setRejectionReason(e.target.value)}
										rows="3"
									/>
									{rejectionError && (
										<div style={{ color: 'red', marginTop: '5px' }}>{rejectionError}</div>
									)}
								</div>
							</div>
							<div className="modal-footer">
								<button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
									Cancel
								</button>
								<button type="button" className="btn btn-primary" onClick={hnadleSubmitRected}>
									Submit
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	)
}

export default TableFilter
